/**
 * Restoration · spectral repair — STFT analyse + ISTFT resynth with masked bins.
 *
 * Killer iZotope-RX-style feature: the user paints rectangles or freehand
 * strokes over the spectrogram of a processed file to remove specific
 * time × frequency artefacts that no per-sample chain caught — a cough
 * during a vinyl track, a microphone bump, a single dropout, a printer
 * hum in the background. The selected cells are replaced with the
 * LOCAL MEDIAN MAGNITUDE of their unmasked neighbours; original phase
 * is preserved (modifying only magnitude avoids the "phasey/swirly"
 * artefacts that pure zero-bins produce).
 *
 * STFT params: FFT 2048 / hop 512 / Hann (synth + analysis both Hann,
 * WOLA-normalised on resynth).
 *
 * Input params:
 *   regions: Array<{ tStart, tEnd, fStart, fEnd, mode? }>
 *            time in seconds, frequency in Hz. mode = 'rect' (default) or
 *            'erase' (sets mask=1 explicitly, used by undo of an earlier
 *            paint without re-running the full chain).
 *
 * The module is registered into the engine like the other modules but is
 * usually invoked once-off (not part of the auto-chain) with a single
 * step: AudioRestorationEngine.runChain(buffer, [{name:'spectralRepair',
 * enabled:true, params:{regions:[...]}}], opts).
 */
(function () {
    'use strict';

    const FFT_SIZE = 2048;
    const HOP = 512;

    /* In-place radix-2 Cooley-Tukey. Duplicated across modules to keep each
       self-contained — exporting a shared FFT across modules would need a
       global namespace that's brittle in face of script-loading ordering. */
    function fft(re, im) {
        const n = re.length;
        for (let i = 1, j = 0; i < n; i++) {
            let bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) {
                let t = re[i]; re[i] = re[j]; re[j] = t;
                t = im[i]; im[i] = im[j]; im[j] = t;
            }
        }
        for (let len = 2; len <= n; len <<= 1) {
            const ang = -2 * Math.PI / len;
            const wlr = Math.cos(ang), wli = Math.sin(ang);
            for (let i = 0; i < n; i += len) {
                let wr = 1, wi = 0;
                const half = len >> 1;
                for (let k = 0; k < half; k++) {
                    const a = i + k;
                    const b = a + half;
                    const xr = re[b] * wr - im[b] * wi;
                    const xi = re[b] * wi + im[b] * wr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi;
                    re[a] += xr;        im[a] += xi;
                    const tw = wr * wlr - wi * wli;
                    wi = wr * wli + wi * wlr;
                    wr = tw;
                }
            }
        }
    }
    function ifft(re, im) {
        const n = re.length;
        fft(im, re);
        for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
    }

    function hannWindow(n) {
        const w = new Float32Array(n);
        for (let i = 0; i < n; i++) w[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (n - 1)));
        return w;
    }

    /**
     * Local-median magnitude. For a masked cell at (frame, bin), gather
     * |X| from unmasked cells in ±radius window, take the median.
     *
     * If fewer than 3 neighbours are unmasked (rare — happens when the
     * user paints a single huge contiguous region), fall back to global
     * median of the bin across all unmasked frames so we don't reach
     * outside the dataset.
     */
    function localMedian(magnitudes, mask, frame, bin, nFrames, nBins, radius) {
        const candidates = [];
        const fLo = Math.max(0, frame - radius);
        const fHi = Math.min(nFrames - 1, frame + radius);
        const bLo = Math.max(0, bin - radius);
        const bHi = Math.min(nBins - 1, bin + radius);
        for (let f = fLo; f <= fHi; f++) {
            for (let b = bLo; b <= bHi; b++) {
                if (mask[f * nBins + b] === 1) {
                    candidates.push(magnitudes[f * nBins + b]);
                }
            }
        }
        if (candidates.length >= 3) {
            candidates.sort(function (a, b) { return a - b; });
            return candidates[Math.floor(candidates.length / 2)];
        }
        // Fall back to bin-wise global median across all unmasked frames
        const all = [];
        for (let f = 0; f < nFrames; f++) {
            if (mask[f * nBins + bin] === 1) all.push(magnitudes[f * nBins + bin]);
        }
        if (all.length) {
            all.sort(function (a, b) { return a - b; });
            return all[Math.floor(all.length / 2)];
        }
        // Whole bin was masked — return 0 (unlikely path)
        return 0;
    }

    function processChannel(samples, sr, regions, signal) {
        const N = samples.length;
        if (N < FFT_SIZE) {
            const out = new Float32Array(N);
            out.set(samples);
            return out;
        }
        const win = hannWindow(FFT_SIZE);
        const winSq = new Float32Array(FFT_SIZE);
        for (let i = 0; i < FFT_SIZE; i++) winSq[i] = win[i] * win[i];

        const nFrames = Math.floor((N - FFT_SIZE) / HOP) + 1;
        const nBins = FFT_SIZE / 2 + 1;

        // 1) STFT — collect magnitudes + complex spectra for all frames
        const mag = new Float32Array(nFrames * nBins);
        const re  = new Float32Array(nFrames * nBins);
        const im  = new Float32Array(nFrames * nBins);
        const fre = new Float32Array(FFT_SIZE);
        const fim = new Float32Array(FFT_SIZE);
        for (let f = 0; f < nFrames; f++) {
            if (signal && signal.aborted) { const e = new Error('aborted'); e.name='AbortError'; throw e; }
            const start = f * HOP;
            for (let i = 0; i < FFT_SIZE; i++) { fre[i] = samples[start + i] * win[i]; fim[i] = 0; }
            fft(fre, fim);
            const off = f * nBins;
            for (let k = 0; k < nBins; k++) {
                const r = fre[k], ii = fim[k];
                re[off + k] = r;
                im[off + k] = ii;
                mag[off + k] = Math.sqrt(r * r + ii * ii);
            }
        }

        // 2) Build mask from regions (1 = keep, 0 = replace)
        const mask = new Uint8Array(nFrames * nBins);
        mask.fill(1);
        const timeStep = HOP / sr; // seconds per frame
        for (let r = 0; r < regions.length; r++) {
            const reg = regions[r];
            const fStart = Math.max(0, Math.floor(reg.tStart / timeStep));
            const fEnd   = Math.min(nFrames - 1, Math.ceil(reg.tEnd / timeStep));
            const bStart = Math.max(0, Math.floor(reg.fStart / sr * FFT_SIZE));
            const bEnd   = Math.min(nBins - 1, Math.ceil(reg.fEnd / sr * FFT_SIZE));
            const newVal = reg.mode === 'erase' ? 1 : 0;
            for (let f = fStart; f <= fEnd; f++) {
                for (let b = bStart; b <= bEnd; b++) {
                    mask[f * nBins + b] = newVal;
                }
            }
        }

        // 3) Magnitude replacement on masked cells (local median)
        const radius = 3;
        for (let f = 0; f < nFrames; f++) {
            const off = f * nBins;
            for (let k = 0; k < nBins; k++) {
                if (mask[off + k] === 0) {
                    const newMag = localMedian(mag, mask, f, k, nFrames, nBins, radius);
                    const oldMag = mag[off + k];
                    if (oldMag > 1e-9) {
                        const gain = newMag / oldMag;
                        re[off + k] *= gain;
                        im[off + k] *= gain;
                    }
                    mag[off + k] = newMag;
                }
            }
        }

        // 4) ISTFT WOLA — mirror Hermitian, ifft, window again, overlap-add,
        //    divide by Σ(win²) for amplitude preservation.
        const out = new Float32Array(N);
        const wolaSum = new Float32Array(N);
        for (let f = 0; f < nFrames; f++) {
            if (signal && signal.aborted) { const e = new Error('aborted'); e.name='AbortError'; throw e; }
            const off = f * nBins;
            for (let i = 0; i < FFT_SIZE; i++) { fre[i] = 0; fim[i] = 0; }
            // DC + Nyquist
            fre[0] = re[off + 0]; fim[0] = im[off + 0];
            fre[FFT_SIZE / 2] = re[off + (FFT_SIZE / 2)]; fim[FFT_SIZE / 2] = im[off + (FFT_SIZE / 2)];
            // Positive bins + Hermitian negatives
            for (let k = 1; k < FFT_SIZE / 2; k++) {
                fre[k] = re[off + k];   fim[k] = im[off + k];
                fre[FFT_SIZE - k] =  re[off + k];
                fim[FFT_SIZE - k] = -im[off + k];
            }
            ifft(fre, fim);
            const start = f * HOP;
            for (let i = 0; i < FFT_SIZE; i++) {
                out[start + i] += fre[i] * win[i];
                wolaSum[start + i] += winSq[i];
            }
        }
        for (let i = 0; i < N; i++) {
            if (wolaSum[i] > 1e-8) out[i] /= wolaSum[i];
            else out[i] = samples[i];
        }
        return out;
    }

    async function process(input, params, ctx) {
        const regions = Array.isArray(params.regions) ? params.regions : [];
        if (!regions.length) {
            // No-op pass-through
            const out0 = ctx.ctx.createBuffer(input.numberOfChannels, input.length, input.sampleRate);
            for (let c = 0; c < input.numberOfChannels; c++) out0.copyToChannel(input.getChannelData(c), c);
            return { output: out0, meta: { regions: 0 } };
        }
        const out = ctx.ctx.createBuffer(input.numberOfChannels, input.length, input.sampleRate);
        let totalCells = 0;
        for (let c = 0; c < input.numberOfChannels; c++) {
            const ch = processChannel(input.getChannelData(c), input.sampleRate, regions, ctx.signal);
            out.getChannelData(c).set(ch);
            await new Promise(function (r) { setTimeout(r, 0); });
        }
        // Estimate cells (frames × bins) painted — useful metric for the impact chip
        for (let r = 0; r < regions.length; r++) {
            const reg = regions[r];
            const sr = input.sampleRate;
            const fSpan = (reg.tEnd - reg.tStart) * sr / HOP;
            const bSpan = (reg.fEnd - reg.fStart) / sr * FFT_SIZE;
            totalCells += Math.max(0, Math.floor(fSpan * bSpan));
        }
        return {
            output: out,
            meta: { regions: regions.length, cellsRepaired: totalCells },
        };
    }

    window.Restoration_spectralRepair = { process: process };
})();
